import { FC } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import type { Link } from 'src/__generated__/graphqlTypes';
import type { Store } from 'app-redux/types/storeTypes';
import MiddleBarMainLogo from 'components/Header/MiddleBar/MainLogo/MiddleBarMainLogo';
import { SearchBar } from 'entities/SearchBar';
import { HamburgerMenuButton } from 'entities/HamburgerMenuButton';
import { HeaderTopBarDesktop, HeaderTopBarMobile } from 'features/HeaderTopBar';
import { ViewerDevice } from 'constants/enums';

import styles from './styles.module.scss';
import NavigationContainerDesktop from './NavigationDesktop';
import QuickLinks from './QuickLinks';
import MobileHamburgerWrapper from './MobileHamburgerWrapper';

import type { IHeader } from '../config/declarations';
import { calculateMobileHeaderHeight } from '../lib';

const Header: FC<IHeader> = ({ header }) => {
  const isMobileViewWidth = useSelector((store: Store) => store.server.app.isMobileViewWidth);
  const viewerDevice = useSelector((store: Store) => store.server.app.viewerDevice);
  const isMobileOrTablet = isMobileViewWidth || (viewerDevice === ViewerDevice.TABLET);

  if (!header) {
    return null;
  }

  if (isMobileOrTablet) {
    const height = calculateMobileHeaderHeight(header);

    return (
      <>
        <MobileHamburgerWrapper header={header} />
        <header style={{ height }} className={cn(styles.header, styles.mobile)}>
          <div className={styles.container}>
            <HeaderTopBarMobile topBar={header.top} />
            <section className={styles.head}>
              <HamburgerMenuButton
                button={header.hamburgerButton}
                className={styles.headHamburger}
              />
              <MiddleBarMainLogo isMobileView mainLogo={header.mainLogoMobile!} />
              <SearchBar
                search={header.searchBox}
                classNameIcon={styles.headSearch}
                isTooltip
              />
            </section>
            <QuickLinks links={header.quickLinksCollection?.items as Array<Link>} />
          </div>
        </header>
      </>
    );
  }

  return (
    <header className={styles.header}>
      <HeaderTopBarDesktop
        topBar={header.top!}
        rating={header.rating}
        auth={header.authorization}
        search={header.searchBox}
      />
      <NavigationContainerDesktop
        search={header.searchBox}
        nav={header.nav}
        logo={header.mainLogo}
      />
    </header>
  );
};

export default Header;
